import { BrandKit, VoiceParams } from 'types'
import Styles from './BrandVoice.module.scss'
import { Text } from 'blaze-ui/Text/Text'
import { Chip } from 'components/Chip/Chip'
import { useFlipperFlag } from 'hooks/useFlipperFlag'
import { BrandVoiceEditable } from './BrandVoiceEditable'
import { ReadOnlyAttribute } from '../components/ReadOnlyAttribute'
import { useMemo } from 'react'
import { useIsSystemRedesign } from 'hooks/useIsSystemRedesign'

type Props = {
  brandKit: BrandKit
  voiceParams?: VoiceParams
  onVoiceUpdate?: (voice: VoiceParams) => void
  isOnboarding?: boolean
}

export const BrandVoice = ({ brandKit, voiceParams: voiceParamsProp, onVoiceUpdate, isOnboarding }: Props) => {
  const isSystemRedesignEnabled = useIsSystemRedesign()
  const isBrandKitUpdatesEnabled = useFlipperFlag('brand_kit_updates')

  const voiceParams = useMemo(() => {
    return (
      voiceParamsProp ??
      brandKit.brand_voice?.voice_params ??
      ({
        name: `${brandKit.name} Brand Voice`,
      } as Partial<VoiceParams>)
    )
  }, [voiceParamsProp, brandKit.brand_voice?.voice_params, brandKit.name])

  if (!isBrandKitUpdatesEnabled) {
    return <BrandVoiceEditable brandKit={brandKit} onVoiceUpdate={onVoiceUpdate} isOnboarding={isOnboarding} />
  }

  const renderValue = (value: string[] | undefined, placeholder: string, isPills = false) => {
    if (!value || value.length === 0) {
      return (
        <Text variant="metadata" color="var(--dark-60)">
          {placeholder}
        </Text>
      )
    }

    if (isPills) {
      return (
        <div className={Styles.pillsContainer}>
          {value.map((item, i) => (
            <Chip
              key={i}
              variant={isSystemRedesignEnabled ? 'square' : 'flat'}
              size="md"
              deletable={false}
              selectable={false}
              className={Styles.readOnlyChip}
            >
              {item}
            </Chip>
          ))}
        </div>
      )
    }

    return (
      <Text variant="secondary" color="var(--dark-80)">
        {value.join(', ')}
      </Text>
    )
  }

  return (
    <div className={Styles.attributes}>
      <ReadOnlyAttribute title="Purpose" description="What your brand stands for and why people should care.">
        {renderValue(voiceParams.purpose, 'No purpose defined')}
      </ReadOnlyAttribute>

      <ReadOnlyAttribute title="Audience" description="Who you're talking to in your marketing.">
        {renderValue(voiceParams.audience, 'No audience defined')}
      </ReadOnlyAttribute>

      <ReadOnlyAttribute title="Tone" description="How your brand sounds in every conversation.">
        {renderValue(voiceParams.tone, 'No tone defined', true)}
      </ReadOnlyAttribute>

      <ReadOnlyAttribute title="Emotions" description="The feelings your marketing should create.">
        {renderValue(voiceParams.emotions, 'No emotions defined', true)}
      </ReadOnlyAttribute>

      <ReadOnlyAttribute title="Character" description="The role your brand plays in customers' lives.">
        {renderValue(voiceParams.character, 'No character defined', true)}
      </ReadOnlyAttribute>

      <ReadOnlyAttribute title="Syntax" description="Your writing style and personality quirks.">
        {renderValue(voiceParams.syntax, 'No syntax defined', true)}
      </ReadOnlyAttribute>

      <ReadOnlyAttribute title="Language" description="How formal or casual you sound.">
        {renderValue(voiceParams.language, 'No language defined', true)}
      </ReadOnlyAttribute>
    </div>
  )
}
